import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { apiErrorMessage, launchApi } from './api';
import { makeId, saveDownloadedFile } from './helpers';
import { help } from './helpTexts';
import { launchColors } from './launchTheme';
import { Assignee, LaunchFiles as LaunchFilesState } from './types';
import AttachmentList from './ui/AttachmentList';
import SectionCard from './ui/SectionCard';
import UploadFilesDialog from './ui/UploadFilesDialog';

interface Props {
  launchId: string;
  files: LaunchFilesState;
  assignees: Assignee[];
  onOpenActivity: (activityId: string) => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
  onNotice: (message: string) => void;
}

const LaunchFiles: React.FC<Props> = ({ launchId, files, assignees, onOpenActivity, onChanged, onError, onNotice }) => {
  const [folderId, setFolderId] = useState('all');
  const [query, setQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [folderDialog, setFolderDialog] = useState<{ mode: 'create' | 'rename' | 'remove'; folderId?: string; name: string } | null>(null);
  const selected = files.folders.some((folder) => folder.id === folderId) ? folderId : 'all';
  const needle = query.trim().toLocaleLowerCase('sv');
  const visible = files.attachments.filter((file) => (
    (selected === 'all' || file.folderId === selected)
    && (!needle || file.fileName.toLocaleLowerCase('sv').includes(needle))
  ));
  const selectedFolder = files.folders.find((folder) => folder.id === selected);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setSaving(true);
    onError(null);
    try {
      await action();
      onChanged();
    } catch (error) {
      onError(apiErrorMessage(error, fallback));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (selectedFiles: File[], targetFolderId: string) => {
    setSaving(true);
    setUploadError(null);
    setProgress(0);
    try {
      await launchApi.uploadFiles(launchId, { commandId: makeId('files'), folderId: targetFolderId, files: selectedFiles }, setProgress);
      setUploadOpen(false);
      onNotice('Filerna har laddats upp.');
      onChanged();
    } catch (error) {
      setUploadError(apiErrorMessage(error, 'Filerna kunde inte laddas upp.'));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const saveFolder = async () => {
    if (!folderDialog) return;
    const name = folderDialog.name.trim();
    if (folderDialog.mode !== 'remove' && !name) return;
    const command = folderDialog.mode === 'create'
      ? { commandId: makeId('folder'), type: 'createFileFolder', folderId: makeId('folder'), name }
      : folderDialog.mode === 'rename'
        ? { commandId: makeId('renameFolder'), type: 'renameFileFolder', folderId: folderDialog.folderId, name }
        : { commandId: makeId('removeFolder'), type: 'removeFileFolder', folderId: folderDialog.folderId };
    await run(async () => {
      await launchApi.fileCommand(launchId, command);
      setFolderDialog(null);
      if (folderDialog.mode === 'remove') setFolderId('all');
      onNotice(folderDialog.mode === 'remove' ? 'Mappen har tagits bort.' : 'Mappen har sparats.');
    }, 'Mappen kunde inte sparas.');
  };

  return (
    <SectionCard
      title="Filer"
      info={help.filesTab}
      subtitle={`${files.attachments.length} filer`}
      action={files.canUpload ? (
        <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={() => { setUploadError(null); setUploadOpen(true); }}>
          Ladda upp
        </Button>
      ) : undefined}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 2 }}>
        <Stack spacing={0.5}>
          <FolderButton selected={selected === 'all'} label="Alla filer" count={files.attachments.length} onClick={() => setFolderId('all')} />
          {files.folders.map((folder) => (
            <FolderButton
              key={folder.id}
              selected={selected === folder.id}
              label={folder.name}
              count={folder.fileCount}
              onClick={() => setFolderId(folder.id)}
            />
          ))}
          {files.canManageFolders && (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ pt: 1 }}>
              <Button size="small" startIcon={<CreateNewFolderOutlinedIcon />} onClick={() => setFolderDialog({ mode: 'create', name: '' })}>Ny mapp</Button>
              {selectedFolder && (
                <Button size="small" onClick={() => setFolderDialog({ mode: 'rename', folderId: selectedFolder.id, name: selectedFolder.name })}>Byt namn</Button>
              )}
              {selectedFolder && selectedFolder.fileCount === 0 && (
                <Button size="small" color="error" onClick={() => setFolderDialog({ mode: 'remove', folderId: selectedFolder.id, name: selectedFolder.name })}>Ta bort mapp</Button>
              )}
            </Stack>
          )}
        </Stack>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Sök filer"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {visible.length === 0 && (
            <Typography sx={{ fontSize: 14, color: launchColors.textSecondary }}>
              {files.attachments.length === 0 ? 'Inga filer uppladdade ännu.' : 'Inga filer matchar sökningen eller mappen.'}
            </Typography>
          )}
          <AttachmentList
            files={visible}
            folders={files.folders}
            assignees={assignees}
            showActivity
            busy={saving}
            onOpenActivity={onOpenActivity}
            onDownload={(file) => {
              void (async () => {
                try {
                  const blob = await launchApi.downloadFile(launchId, file.id);
                  saveDownloadedFile(blob, file.fileName);
                } catch (error) {
                  onError(apiErrorMessage(error, 'Filen kunde inte laddas ner.'));
                }
              })();
            }}
            onDelete={(file) => {
              void run(async () => {
                await launchApi.fileCommand(launchId, { commandId: makeId('removeFile'), type: 'removeFile', attachmentId: file.id });
                onNotice('Filen har tagits bort.');
              }, 'Filen kunde inte tas bort.');
            }}
            onMove={(file, targetFolderId) => {
              void run(async () => {
                await launchApi.fileCommand(launchId, { commandId: makeId('moveFile'), type: 'moveFile', attachmentId: file.id, folderId: targetFolderId });
                onNotice('Filen har flyttats.');
              }, 'Filen kunde inte flyttas.');
            }}
          />
        </Stack>
      </Box>
      <UploadFilesDialog
        open={uploadOpen}
        folders={files.folders}
        defaultFolderId={selected === 'all' ? 'folder-ovrigt' : selected}
        saving={saving}
        progress={progress}
        error={uploadError}
        rules={files.rules}
        onClose={() => setUploadOpen(false)}
        onSubmit={(selectedFiles, targetFolderId) => { void upload(selectedFiles, targetFolderId); }}
      />
      <Dialog open={Boolean(folderDialog)} onClose={() => setFolderDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {folderDialog?.mode === 'remove' ? 'Ta bort mapp' : folderDialog?.mode === 'rename' ? 'Byt namn på mapp' : 'Ny mapp'}
        </DialogTitle>
        <DialogContent>
          {folderDialog?.mode === 'remove' ? (
            <Typography sx={{ fontSize: 14 }}>{folderDialog.name} tas bort.</Typography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Mappnamn"
              value={folderDialog?.name || ''}
              onChange={(event) => setFolderDialog((current) => current ? { ...current, name: event.target.value } : current)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialog(null)}>Avbryt</Button>
          <Button variant="contained" disabled={saving || (folderDialog?.mode !== 'remove' && !folderDialog?.name.trim())} onClick={() => { void saveFolder(); }}>
            {folderDialog?.mode === 'remove' ? 'Ta bort' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

const FolderButton: React.FC<{ selected: boolean; label: string; count: number; onClick: () => void }> = ({ selected, label, count, onClick }) => (
  <Button
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      justifyContent: 'space-between',
      color: launchColors.navy,
      bgcolor: selected ? launchColors.softNavy : 'transparent',
      fontWeight: selected ? 700 : 500,
    }}
  >
    <span>{label}</span>
    <Box component="span" sx={{ color: launchColors.textSecondary, fontWeight: 500 }}>{count}</Box>
  </Button>
);

export default LaunchFiles;
